/**
 * FabricRenderer - Uniwersalny komponent renderujący Layout JSON na Fabric.js canvas.
 *
 * Tekst jest renderowany przez Fabric.js Textbox z splitByGrapheme: true,
 * co gwarantuje że żaden tekst nie przekroczy ustawionej szerokości.
 * Brak customowych funkcji do zmiany fontSize - rozmiar pochodzi z definicji layoutu.
 */

import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Canvas, Image, Textbox, Rect, Pattern } from 'fabric';
import { applyWordSpacing } from './textSpacing';
import { parseBoldMarkers } from './boldMarkers';
import jsPDF from 'jspdf';
import { parseTextDSL, checkCondition, parseColor } from '../textDSL/parser';
import { calculatePosition, calculateFontSize } from './positionCalculator';
import { loadFonts, collectLayoutFonts } from './fontLoader';
import { snapshotLayoutObject, applyLayoutProps, createAddedText, collectLayoutChanges } from './layoutChanges';
import { buildTextMap, expandRangeToFields } from '../textDSL/segments';

// Import domyślnych obrazków
import krzyz1 from '../../../../img/krzyz1.png';
import whiteCross from '../../../../img/whiteCross.png';
import crossPlaqueDefaultImage from '../../../../img/crossPlaqueDefaultImage.png';

// Mapa domyślnych obrazków
const defaultImages = {
    'krzyz1.png': krzyz1,
    'whiteCross.png': whiteCross,
    'crossPlaqueDefaultImage.png': crossPlaqueDefaultImage
};

const EMPTY_DATA = {};

// Materiały, których PDF zostaje w PNG (przezroczystość)
const PDF_PNG_LAYOUTS = new Set(['obituary-black']);

const dataUrlToBlob = (dataUrl) => {
    const [header, base64] = dataUrl.split(',');
    const mime = header.match(/data:([^;]+)/)?.[1] || 'application/octet-stream';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
};
const EMPTY_OVERRIDES = {};
const A4_ORIENTATION_BY_LAYOUT = {
    'obituary-minimalizm': 'landscape',
    'obituary-pogrubione': 'landscape',
    'obituary-black': 'landscape',
    'thank-you': 'landscape',
    'cross-plaque': 'portrait',
    'snitch-plaque': 'portrait',
    'coffin-plaque': 'portrait',
    'urn-plaque': 'portrait',
};
const JPG_EXPORT_SIZES = {
    'obituary-minimalizm': { width: 1754, height: 1240 },
    'obituary-pogrubione': { width: 1754, height: 1240 },
    'obituary-black': { width: 1754, height: 1240 },
    'thank-you': { width: 1754, height: 1240 },
    'cross-plaque': { width: 1240, height: 1754 },
    'snitch-plaque': { width: 846, height: 540 },
    'coffin-plaque': { width: 846, height: 540 },
    'urn-plaque': { width: 846, height: 540 },
};
const PDF_EXPORT_SIZES = {
    'obituary-minimalizm': { width: 1754, height: 1240 },
    'obituary-pogrubione': { width: 1754, height: 1240 },
    'obituary-black': { width: 1754, height: 1240 },
    'thank-you': { width: 1754, height: 1240 },
    'cross-plaque': { width: 1240, height: 1754 },
    'snitch-plaque': { width: 2093, height: 2160, contentLeft: 100, contentTop: 100 },
    'coffin-plaque': { width: 1216, height: 2160, contentLeft: 100, contentTop: 100, pdfScale: 1.05 },
    'urn-plaque': { width: 2093, height: 2160, contentLeft: 100, contentTop: 100 },
};

/**
 * Pobierz domyślny obrazek
 */
function getDefaultImage(imageName) {
    return defaultImages[imageName] || krzyz1;
}

/**
 * Załaduj obrazek jako element Fabric.Image
 */
async function loadImage(src, token) {
    let imageSrc = src;

    // Dodaj token jeśli to URL z backendu i token jeszcze nie jest w URL
    if (src && !src.startsWith('data:') && !src.startsWith('blob:') && !src.startsWith('/') && token && !src.includes('token=')) {
        imageSrc = src.includes('?') ? `${src}&token=${token}` : `${src}?token=${token}`;
    }

    try {
        const fabricImage = await Image.fromURL(imageSrc, { crossOrigin: 'anonymous' });
        return fabricImage;
    } catch (e) {
        console.warn(`Nie udało się załadować obrazka: ${imageSrc}`, e);
        return null;
    }
}

/**
 * Renderuj pojedynczy element layoutu.
 *
 * Logika zawijania tekstu (odwzorowanie starych komponentów HTML/CSS):
 *
 * - singleLine: true → tekst NIGDY nie zawija się. fontSize maleje (Textbox.getLineWidth)
 *   aż cały tekst mieści się w jednej linii. Dolna granica: minFontSize.
 *
 * - fitWidth: true (z singleLine) → ramka napisu ma szerokość samego tekstu, a nie całego pola;
 *   krawędź wyrównania (textAlign) zostaje na miejscu. Pole (position.width) to wtedy maksymalna szerokość.
 *
 * - verticalCenter: true → position.top to środek napisu w pionie (zmiana wielkości liter go nie przesuwa).
 *
 * - widthOnlyControls: true → tylko boczne uchwyty zmiany szerokości (bez skalowania narożnikami i obrotu).
 *
 * - singleLine: false/brak → tekst zawija się naturalnie na granicach słów
 *   (splitByGrapheme: false). Znaki \n w treści tworzą jawne łamanie linii
 *   (obsługiwane natywnie przez Fabric.js Textbox).
 */
async function renderElement(element, data, layout, editable, token, overrides = {}) {
    const { canvas: canvasDims, theme } = layout;
    const position = calculatePosition(element.position, canvasDims);

    if (element.type === 'text') {
        let parsedText;
        if (overrides[element.id] !== undefined && overrides[element.id] !== '') {
            parsedText = overrides[element.id];
        } else {
            parsedText = parseTextDSL(element.content || '', data);
        }

        if (!parsedText.trim()) return null;

        const spacedText = applyWordSpacing(parsedText, element.style?.wordSpacing);
        const { cleanText, styles: boldStyles, hasBold } = parseBoldMarkers(spacedText);
        const textToRender = hasBold ? cleanText : spacedText;

        const baseFontSize = calculateFontSize(element.style?.fontSize, canvasDims.width);
        const fill = parseColor(element.style?.fill, theme);
        const targetWidth = position.width || 200;
        const singleLine = element.style?.singleLine || false;
        const minFontSize = element.style?.minFontSize || 8;

        let finalFontSize = baseFontSize;
        let boxLeft = position.left;
        let boxWidth = targetWidth;
        let naturalWidth = 0; // szerokość tekstu w bazowym rozmiarze liter

        if (singleLine) {
            // Pomiar jako jedna linia - duży width zapobiega zawijaniu przez Fabric
            const measure = new Textbox(textToRender, {
                width: 50000,
                fontSize: finalFontSize,
                fontFamily: element.style?.fontFamily || 'Arial',
                fontWeight: element.style?.fontWeight || 'normal',
                fontStyle: element.style?.fontStyle || 'normal',
            });
            measure.initDimensions();
            naturalWidth = measure.getLineWidth(0);

            // Zmniejszaj fontSize aż tekst zmieści się w targetWidth
            while (measure.getLineWidth(0) > targetWidth && finalFontSize > minFontSize) {
                finalFontSize -= 1;
                measure.set('fontSize', finalFontSize);
                measure.initDimensions();
            }

            // Ramka dopasowana do tekstu – przesunięcie zależnie od wyrównania, żeby napis nie „skakał”
            if (element.style?.fitWidth) {
                const textWidth = Math.ceil(measure.getLineWidth(0)) + 4;
                if (textWidth < targetWidth) {
                    const align = element.style?.textAlign || 'center';
                    const spare = targetWidth - textWidth;
                    boxLeft = position.left + (align === 'right' ? spare : align === 'left' ? 0 : spare / 2);
                    boxWidth = textWidth;
                }
            }
        }

        const textbox = new Textbox(textToRender, {
            top: position.top,
            left: boxLeft,
            width: boxWidth,
            fontSize: finalFontSize,
            fontFamily: element.style?.fontFamily || 'Arial',
            fontWeight: element.style?.fontWeight || 'normal',
            fontStyle: element.style?.fontStyle || 'normal',
            textAlign: element.style?.textAlign || 'center',
            fill,
            splitByGrapheme: false,
            selectable: editable && element.editable !== false,
            editable: editable && element.editable !== false,
        });

        if (hasBold) {
            textbox.styles = boldStyles;
        }

        if (element.style?.verticalCenter) {
            textbox.initDimensions();
            textbox.set({ top: position.top - textbox.height / 2 });
        }

        if (singleLine && element.style?.fitWidth && naturalWidth > 0) {
            // Łamanie po znakach – inaczej Fabric nie pozwala zwęzić pola poniżej najdłuższego słowa.
            // Litery dopasowuje na bieżąco obsługa 'object:resizing' (tekst zostaje w jednej linii).
            textbox.set({ splitByGrapheme: true });
            textbox.fitMeta = { baseFontSize, naturalWidth, minFontSize };
        }

        if (element.style?.widthOnlyControls) {
            textbox.setControlsVisibility({ tl: false, tr: false, bl: false, br: false, mt: false, mb: false, mtr: false });
            textbox.set({ lockRotation: true });
        }

        // Zmniejszaj fontSize aż tekst zmieści się w maxHeight
        if (element.style?.maxHeight) {
            const maxHeight = element.style.maxHeight;
            textbox.initDimensions();
            while (textbox.height > maxHeight && finalFontSize > minFontSize) {
                finalFontSize -= 1;
                textbox.set('fontSize', finalFontSize);
                textbox.initDimensions();
            }
        }

        return textbox;
    }

    if (element.type === 'image') {
        let imageSrc = parseTextDSL(element.content || '', data);

        if (!imageSrc || imageSrc === '') {
            const defaultMatch = element.content?.match(/\{[^|]+\|default:([^}]+)\}/);
            if (defaultMatch) {
                imageSrc = getDefaultImage(defaultMatch[1]);
            }
        }

        if (!imageSrc.startsWith('data:') && !imageSrc.startsWith('blob:') && !imageSrc.startsWith('http') && !imageSrc.startsWith('/')) {
            imageSrc = getDefaultImage(imageSrc);
        }

        const fabricImage = await loadImage(imageSrc, token);

        if (fabricImage) {
            fabricImage.set({
                top: position.top,
                left: position.left,
                selectable: editable && element.editable !== false
            });

            if (position.width) {
                fabricImage.scaleToWidth(position.width);
            }

            return fabricImage;
        }

        return null;
    }

    if (element.type === 'rect') {
        const rect = new Rect({
            top: position.top,
            left: position.left,
            width: position.width || 100,
            height: element.style?.height || 2,
            fill: element.style?.fill || '#000000',
            selectable: false,
            evented: false,
            hasControls: false,
            hasBorders: false,
        });
        return rect;
    }

    if (element.type === 'pattern') {
        const svgString = element.style?.svgString;
        const patternHeight = element.style?.patternHeight || 20;
        if (!svgString) return null;

        const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
        const img = new window.Image();
        await new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
            img.src = dataUrl;
        });

        const pattern = new Pattern({ source: img, repeat: 'repeat-x' });
        const rect = new Rect({
            top: position.top,
            left: position.left,
            width: position.width || 100,
            height: patternHeight,
            fill: pattern,
            selectable: false,
            evented: false,
            hasControls: false,
            hasBorders: false,
        });
        return rect;
    }

    return null;
}

const FabricRenderer = forwardRef(({
    layout,
    data = EMPTY_DATA,
    templateUrl,
    editable = false,
    overrides = EMPTY_OVERRIDES,
    // Wzór zakładu (patrz layoutChanges.js) - nakładany na wzór z aplikacji
    patternChanges = null,
    // Poprawki zmarłego - nakładane na wzór zakładu
    layoutChanges = null,
    onElementSelect,
    onElementChange,
    className,
    style,
    isMirrorImage = false,
    fixedHeight = null,
    activeElementId = null,
    // Wywoływane po każdym pełnym narysowaniu (np. paczka ZIP czeka na nie przed eksportem)
    onRendered = null,
    // Tryb podglądu (editable=false): obramowanie wskazanego napisu i klik w napis („Edytuj tekst”)
    highlightElementId = null,
    clickableElementIds = null,
    onElementClick = null,
}, ref) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const fabricCanvasRef = useRef(null);
    const isMountedRef = useRef(false);
    const isRenderingRef = useRef(false);
    const pendingRenderRef = useRef(false);
    const dataRef = useRef(data);
    const overridesRef = useRef(overrides);
    const layoutChangesRef = useRef(layoutChanges);
    const onRenderedRef = useRef(onRendered);
    // Czy podgląd obsługuje klik w napisy - ustalane raz, przy tworzeniu canvasu
    const isClickablePreviewRef = useRef(Boolean(onElementClick));
    onRenderedRef.current = onRendered;
    const patternChangesRef = useRef(patternChanges);
    // Stan elementów po nałożeniu wzoru zakładu (przed poprawkami zmarłego) - punkt odniesienia dla getLayoutChanges
    const baseSnapshotsRef = useRef(new Map());
    // Stan elementów wzoru z aplikacji - punkt odniesienia przy zapisie jako wzór zakładu
    const builtinSnapshotsRef = useRef(new Map());
    // Mapy tekstu napisów (textDSL/segments.js) - do zapisu stylów fragmentów jako „całe pole”
    const textMapsRef = useRef(new Map());
    // Zaznaczony element trzymany przez rodzica – przywracany po każdym przerysowaniu
    const activeElementIdRef = useRef(activeElementId);
    // Najnowsza wersja renderLayout – zaległe przerysowanie musi użyć aktualnego canvasu i layoutu
    const renderLayoutRef = useRef(null);
    activeElementIdRef.current = activeElementId;
    const [canvas, setCanvas] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [elementsMap, setElementsMap] = useState(new Map());
    const [scale, setScale] = useState(1);

    const token = localStorage.getItem('token');

    useEffect(() => { dataRef.current = data; }, [data]);
    useEffect(() => { overridesRef.current = overrides; }, [overrides]);
    useEffect(() => { layoutChangesRef.current = layoutChanges; }, [layoutChanges]);
    useEffect(() => { patternChangesRef.current = patternChanges; }, [patternChanges]);

    const canvasWidth = layout?.canvas?.width || 1000;
    const canvasHeight = layout?.canvas?.height || 707;

    // Funkcja odbicia lustrzanego
    // Format wyniku jak źródła - wcześniej zawsze PNG, więc odbity „JPG” był w środku PNG-iem
    function mirrorImage(dataUrl, width, height, type = 'image/png', quality = 1.0) {
        return new Promise((resolve) => {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const ctx = tempCanvas.getContext('2d');

            const img = new window.Image();
            img.onload = () => {
                if (type === 'image/jpeg') {
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, width, height);
                }
                ctx.translate(width, 0);
                ctx.scale(-1, 1);
                ctx.drawImage(img, 0, 0);
                resolve(tempCanvas.toDataURL(type, quality));
            };
            img.onerror = () => resolve(dataUrl);
            img.src = dataUrl;
        });
    }

    function composeExportImage(dataUrl, {
        width,
        height,
        contentLeft = 0,
        contentTop = 0,
        type = 'image/png',
        quality = 1.0,
    }) {
        return new Promise((resolve) => {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const ctx = tempCanvas.getContext('2d');

            const img = new window.Image();
            img.onload = () => {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, width, height);

                const drawWidth = Math.min(img.width, width - contentLeft);
                const drawHeight = Math.min(img.height, height - contentTop);
                ctx.drawImage(img, contentLeft, contentTop, drawWidth, drawHeight);
                resolve(tempCanvas.toDataURL(type, quality));
            };
            img.onerror = () => resolve(dataUrl);
            img.src = dataUrl;
        });
    }

    async function exportCanvasImage({
        format = 'png',
        targetSize,
        multiplier = 4,
        quality = 1.0,
    } = {}) {
        if (!canvas) return null;

        const shouldKeepSourceSize = targetSize?.contentLeft !== undefined || targetSize?.contentTop !== undefined;
        const effectiveMultiplier = shouldKeepSourceSize
            ? 1
            : targetSize
            ? Math.max(canvasWidth ? targetSize.width / canvasWidth : 1, canvasHeight ? targetSize.height / canvasHeight : 1, 1)
            : multiplier;

        let dataUrl = canvas.toDataURL({
            format,
            quality,
            multiplier: effectiveMultiplier,
        });

        if (targetSize) {
            dataUrl = await composeExportImage(dataUrl, {
                ...targetSize,
                type: format === 'jpeg' ? 'image/jpeg' : 'image/png',
                quality,
            });
        }

        if (isMirrorImage) {
            const mirrorWidth = targetSize?.width || canvasWidth * effectiveMultiplier;
            const mirrorHeight = targetSize?.height || canvasHeight * effectiveMultiplier;
            dataUrl = await mirrorImage(dataUrl, mirrorWidth, mirrorHeight, format === 'jpeg' ? 'image/jpeg' : 'image/png', quality);
        }

        return dataUrl;
    }

    async function buildPDF() {
        if (!canvas) return null;

        canvas.discardActiveObject();
        canvas.renderAll();

        const exportSize = PDF_EXPORT_SIZES[layout?.id] || {
            width: canvasWidth * 4,
            height: canvasHeight * 4,
        };
        // JPEG 95% - kilkukrotnie mniejszy PDF bez widocznej różnicy w druku.
        // Nekrolog czarny zostaje w PNG, bo potrzebuje przezroczystości (bez białego tła).
        const keepTransparency = PDF_PNG_LAYOUTS.has(layout?.id);
        const imgData = await exportCanvasImage({
            format: keepTransparency ? 'png' : 'jpeg',
            quality: keepTransparency ? 1.0 : 0.95,
            targetSize: exportSize,
        });
        const orientation = A4_ORIENTATION_BY_LAYOUT[layout?.id]
            || (layout?.canvas?.orientation === 'portrait' ? 'portrait' : 'landscape');
        const pdf = new jsPDF(orientation, 'pt', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const imgWidth = pdfWidth * (exportSize.pdfScale || 1);
        const imgHeight = imgWidth * (exportSize.height / exportSize.width);

        pdf.addImage(imgData, keepTransparency ? 'PNG' : 'JPEG', 0, 0, imgWidth, imgHeight);
        return pdf;
    }

    async function buildJPGDataUrl() {
        if (!canvas) return null;

        canvas.discardActiveObject();
        canvas.renderAll();

        return exportCanvasImage({
            format: 'jpeg',
            targetSize: JPG_EXPORT_SIZES[layout?.id],
            quality: 1.0,
        });
    }

    const getTextMap = elementId => (elementId ? textMapsRef.current.get(elementId) || null : null);

    // Expose metody do rodzica przez ref
    useImperativeHandle(ref, () => ({
        getCanvas: () => canvas,

        toDataURL: (options) => canvas?.toDataURL(options),

        exportPNG: async (multiplier = 4) => {
            if (!canvas) return null;
            canvas.discardActiveObject();
            canvas.renderAll();

            return exportCanvasImage({ format: 'png', multiplier });
        },

        exportPDF: async (filename = 'dokument.pdf') => {
            const pdf = await buildPDF();
            if (pdf) pdf.save(filename);
        },

        exportJPG: async (filename = 'dokument.jpg') => {
            const imgData = await buildJPGDataUrl();
            if (!imgData) return;

            const link = document.createElement('a');
            link.href = imgData;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        // Pliki w pamięci (bez pobierania) - do paczki ZIP
        exportPDFBlob: async () => {
            const pdf = await buildPDF();
            return pdf ? pdf.output('blob') : null;
        },

        exportJPGBlob: async () => {
            const imgData = await buildJPGDataUrl();
            return imgData ? dataUrlToBlob(imgData) : null;
        },

        getElementValue: (elementId) => {
            const fabricObj = elementsMap.get(elementId);
            return fabricObj?.text || fabricObj?.getSrc?.() || null;
        },

        setElementValue: (elementId, value) => {
            const fabricObj = elementsMap.get(elementId);
            if (fabricObj && fabricObj.type === 'textbox') {
                fabricObj.set('text', value);
                canvas?.renderAll();
            }
        },

        rerender: () => {
            renderLayout();
        },

        // Poprawki zmarłego: zmiany układu względem wzoru (z aplikacji lub zakładu)
        getLayoutChanges: () => (
            canvas ? collectLayoutChanges(canvas.getObjects(), baseSnapshotsRef.current, getTextMap) : null
        ),

        // Cały aktualny układ względem wzoru z aplikacji - do zapisania jako wzór zakładu
        getPatternChanges: () => (
            canvas ? collectLayoutChanges(canvas.getObjects(), builtinSnapshotsRef.current, getTextMap).layoutChanges : null
        ),

        // Zaznaczenie w napisie rozszerzone do całych pól (np. „19.10” → cała data mszy)
        expandSelectionToFields: (fabricObject, start, end) => {
            const textMap = getTextMap(fabricObject?.elementId);
            if (!textMap || textMap.text !== fabricObject.text) return { start, end };
            return expandRangeToFields(textMap, start, end);
        },

        // Przerysuj bez zapisanych zmian układu (wersja z formularza)
        resetLayout: () => {
            layoutChangesRef.current = null;
            renderLayout();
        }
    }));

    // Renderowanie layoutu
    const renderLayout = useCallback(async () => {
        if (!canvas || !layout || !isMountedRef.current) return;

        if (isRenderingRef.current) {
            pendingRenderRef.current = true;
            return;
        }
        isRenderingRef.current = true;
        setIsLoading(true);

        await new Promise(resolve => setTimeout(resolve, 0));

        // Jeśli canvas został wymieniony (zmiana wymiarów) lub komponent odmontowany — przerwij
        if (!isMountedRef.current || !fabricCanvasRef.current || fabricCanvasRef.current !== canvas) {
            isRenderingRef.current = false;
            if (pendingRenderRef.current && isMountedRef.current) {
                pendingRenderRef.current = false;
                window.requestAnimationFrame(() => renderLayoutRef.current?.());
            }
            return;
        }

        canvas.clear();
        canvas.backgroundColor = '#fff';

        try {
            // Załaduj czcionki (normal/bold, prosta/kursywa) PRZED renderowaniem elementów -
            // także te wybrane w edytorze układu
            await loadFonts(collectLayoutFonts(layout, patternChangesRef.current, layoutChangesRef.current));

            // Załaduj tło
            if (templateUrl) {
                const bgImage = await loadImage(templateUrl, token);
                if (bgImage && isMountedRef.current) {
                    bgImage.scaleToWidth(canvasWidth);
                    bgImage.scaleToHeight(canvasHeight);
                    const bgWidth = bgImage.getScaledWidth();
                    const bgHeight = bgImage.getScaledHeight();
                    canvas.backgroundImage = bgImage;
                    canvas.backgroundImage.set({
                        left: (canvasWidth - bgWidth) / 2,
                        top: (canvasHeight - bgHeight) / 2
                    });
                }
            }

            if (!isMountedRef.current) return;

            const currentData = dataRef.current;
            const currentOverrides = overridesRef.current;
            const mergedData = { ...currentData, ...currentOverrides };

            // Trzy warstwy: wzór z aplikacji (layout) → wzór zakładu (patternChanges) → poprawki zmarłego (layoutChanges).
            // Stan po każdej warstwie zapamiętujemy, żeby umieć odczytać zmiany względem właściwej warstwy.
            const newElementsMap = new Map();
            const currentPatternChanges = patternChangesRef.current;
            const currentLayoutChanges = layoutChangesRef.current;
            const patternRemovedIds = new Set(currentPatternChanges?.removed || []);
            const removedIds = new Set(currentLayoutChanges?.removed || []);
            const newBuiltinSnapshots = new Map();
            const newBaseSnapshots = new Map();
            // Mapy tekstu (które litery pochodzą z którego pola) - tylko dla napisów generowanych z szablonu
            const newTextMaps = new Map();

            for (const element of layout.elements || []) {
                if (element.condition && !checkCondition(element.condition, mergedData)) {
                    continue;
                }

                const fabricObject = await renderElement(element, mergedData, layout, editable, token, currentOverrides);

                if (fabricObject) {
                    fabricObject.elementId = element.id;
                    fabricObject.elementLabel = element.label;

                    // Tekst przepisany ręcznie (override) nie ma już pól - wtedy brak mapy.
                    // Override identyczny z tekstem z formularza (np. niezmienione pole w „Edytuj tekst”) się nie liczy.
                    const overrideText = currentOverrides[element.id];
                    const hasOverride = overrideText !== undefined && overrideText !== ''
                        && overrideText !== parseTextDSL(element.content || '', mergedData);
                    const textMap = element.type === 'text' && !hasOverride
                        ? buildTextMap(element.content || '', mergedData, element.style?.wordSpacing)
                        : null;
                    if (textMap) newTextMaps.set(element.id, textMap);

                    newBuiltinSnapshots.set(element.id, snapshotLayoutObject(fabricObject, textMap));
                    if (patternRemovedIds.has(element.id)) continue;
                    applyLayoutProps(fabricObject, currentPatternChanges?.elements?.[element.id], textMap);

                    // Stan bazowy zapisujemy także dla usuniętych elementów - inaczej kolejny zapis
                    // „zapomniałby”, że zostały usunięte
                    newBaseSnapshots.set(element.id, snapshotLayoutObject(fabricObject, textMap));
                    if (removedIds.has(element.id)) continue;

                    applyLayoutProps(fabricObject, currentLayoutChanges?.elements?.[element.id], textMap);
                    canvas.add(fabricObject);
                    newElementsMap.set(element.id, fabricObject);
                }
            }

            // Napisy dodane we wzorze zakładu są częścią bazy dla poprawek zmarłego
            (currentPatternChanges?.added || []).forEach((definition) => {
                if (definition?.kind !== 'text') return;
                const overrideText = currentOverrides[definition.id];
                const fabricObject = createAddedText(
                    overrideText ? { ...definition, text: overrideText } : definition,
                    editable,
                );
                newBaseSnapshots.set(definition.id, snapshotLayoutObject(fabricObject, null));
                if (removedIds.has(definition.id)) return;
                applyLayoutProps(fabricObject, currentLayoutChanges?.elements?.[definition.id]);
                canvas.add(fabricObject);
            });

            (currentLayoutChanges?.added || []).forEach((definition) => {
                if (definition?.kind === 'text') {
                    canvas.add(createAddedText(definition, editable));
                }
            });
            builtinSnapshotsRef.current = newBuiltinSnapshots;
            baseSnapshotsRef.current = newBaseSnapshots;
            textMapsRef.current = newTextMaps;

            if (!isMountedRef.current) return;

            const activeObject = editable && activeElementIdRef.current
                ? newElementsMap.get(activeElementIdRef.current)
                : null;
            if (activeObject) {
                canvas.setActiveObject(activeObject);
            }

            setElementsMap(newElementsMap);
            canvas.renderAll();
            setIsLoading(false);
            if (!pendingRenderRef.current) onRenderedRef.current?.();
        } finally {
            isRenderingRef.current = false;
            if (pendingRenderRef.current && isMountedRef.current) {
                pendingRenderRef.current = false;
                window.requestAnimationFrame(() => renderLayoutRef.current?.());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas, layout, templateUrl, editable, token, canvasWidth, canvasHeight]);
    renderLayoutRef.current = renderLayout;

    // Inicjalizacja canvas
    useEffect(() => {
        isMountedRef.current = true;

        if (!containerRef.current || !layout) return;

        const cleanup = () => {
            if (fabricCanvasRef.current) {
                try {
                    fabricCanvasRef.current.dispose();
                } catch (e) {
                    console.warn('Error disposing canvas:', e);
                }
                fabricCanvasRef.current = null;
            }
            if (containerRef.current) {
                const canvasElements = containerRef.current.querySelectorAll('canvas, .canvas-container');
                canvasElements.forEach(el => el.remove());
            }
            setCanvas(null);
        };

        cleanup();

        const canvasEl = document.createElement('canvas');
        containerRef.current.appendChild(canvasEl);

        try {
            const initCanvas = new Canvas(canvasEl, {
                width: canvasWidth,
                height: canvasHeight,
                backgroundColor: '#fff',
                selection: editable,
                // Podgląd z klikaniem napisów („Edytuj tekst”) też musi odbierać zdarzenia myszy
                interactive: editable || isClickablePreviewRef.current,
                // …ale bez blokowania przewijania strony palcem na telefonie
                allowTouchScrolling: !editable,
            });

            if (!isMountedRef.current) {
                initCanvas.dispose();
                canvasEl.remove();
                return;
            }

            fabricCanvasRef.current = initCanvas;
            canvasRef.current = canvasEl;
            setCanvas(initCanvas);
        } catch (e) {
            console.error('Error initializing canvas:', e);
            canvasEl.remove();
        }

        return () => {
            isMountedRef.current = false;
            cleanup();
        };
    }, [canvasWidth, canvasHeight, editable, layout?.id]);

    // Renderowanie po inicjalizacji canvas
    useEffect(() => {
        if (canvas && layout) {
            renderLayout();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas, layout, templateUrl]);

    useEffect(() => {
        if (canvas && layout) {
            renderLayout();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, overrides, layoutChanges, patternChanges]);

    // Obsługa zdarzeń edycji
    useEffect(() => {
        if (!canvas || !editable) return;

        const handleSelection = (e) => {
            if (onElementSelect && e.selected?.[0]) {
                onElementSelect(e.selected[0].elementId, e.selected[0]);
            }
        };

        const handleSelectionCleared = () => {
            // canvas.clear() przy przerysowaniu też czyści zaznaczenie – to nie jest decyzja użytkownika
            if (isRenderingRef.current) return;
            if (onElementSelect) {
                onElementSelect(null, null);
            }
        };

        const handleModified = (e) => {
            if (onElementChange && e.target) {
                onElementChange(e.target.elementId, {
                    text: e.target.text,
                    ...e.target.toObject(),
                    // 'drag', 'resizing', 'scale'… – rodzic może odróżnić przesunięcie od zmiany szerokości
                    action: e.action ?? e.transform?.action,
                });
            }
        };

        // Napis z dopasowaną szerokością: przeciąganie bocznego uchwytu zmienia wielkość liter
        const handleResizing = (e) => {
            const target = e.target;
            if (!target?.fitMeta) return;
            const { baseFontSize, naturalWidth, minFontSize } = target.fitMeta;
            const fitted = baseFontSize * Math.max(0, target.width - 4) / naturalWidth;
            const nextFontSize = Math.max(minFontSize, Math.min(baseFontSize, fitted));
            if (Math.abs(nextFontSize - target.fontSize) > 0.1) {
                target.set('fontSize', nextFontSize);
                target.initDimensions();
                target.setCoords();
            }
        };

        canvas.on('selection:created', handleSelection);
        canvas.on('object:resizing', handleResizing);
        canvas.on('selection:updated', handleSelection);
        canvas.on('selection:cleared', handleSelectionCleared);
        canvas.on('object:modified', handleModified);
        canvas.on('text:changed', handleModified);

        return () => {
            canvas.off('selection:created', handleSelection);
            canvas.off('object:resizing', handleResizing);
            canvas.off('selection:updated', handleSelection);
            canvas.off('selection:cleared', handleSelectionCleared);
            canvas.off('object:modified', handleModified);
            canvas.off('text:changed', handleModified);
        };
    }, [canvas, editable, onElementSelect, onElementChange]);

    // Zaznaczenie wybrane poza podglądem (np. z listy elementów)
    useEffect(() => {
        if (!canvas || !editable || isRenderingRef.current) return;
        if (canvas !== fabricCanvasRef.current) return;
        const current = canvas.getActiveObject();
        if (!activeElementId) {
            if (current) {
                canvas.discardActiveObject();
                canvas.requestRenderAll();
            }
            return;
        }
        const target = elementsMap.get(activeElementId);
        if (target && current !== target) {
            canvas.setActiveObject(target);
            canvas.requestRenderAll();
        }
    }, [canvas, editable, activeElementId, elementsMap]);

    // Podświetlenie napisu w podglądzie - nakładka HTML nad canvasem, więc nigdy nie trafia do PDF/JPG
    const [highlightBox, setHighlightBox] = useState(null);
    useEffect(() => {
        const target = highlightElementId ? elementsMap.get(highlightElementId) : null;
        const lowerCanvas = canvas?.lowerCanvasEl;
        if (!target || !lowerCanvas || !containerRef.current || isLoading) {
            setHighlightBox(null);
            return;
        }
        const containerRect = containerRef.current.getBoundingClientRect();
        const canvasRect = lowerCanvas.getBoundingClientRect();
        const cssScale = canvasWidth ? canvasRect.width / canvasWidth : 1;
        const bounds = target.getBoundingRect();
        const padding = 6;
        setHighlightBox({
            left: canvasRect.left - containerRect.left + bounds.left * cssScale - padding,
            top: canvasRect.top - containerRect.top + bounds.top * cssScale - padding,
            width: bounds.width * cssScale + padding * 2,
            height: bounds.height * cssScale + padding * 2,
        });
    }, [canvas, highlightElementId, elementsMap, scale, isLoading, canvasWidth]);

    // Klik w napis w trybie podglądu
    useEffect(() => {
        if (!canvas || editable || !onElementClick) return undefined;
        const clickable = new Set(clickableElementIds || []);
        elementsMap.forEach((object, elementId) => {
            object.hoverCursor = clickable.has(elementId) ? 'pointer' : 'default';
        });
        const handleMouseDown = (event) => {
            const elementId = event.target?.elementId;
            if (elementId && clickable.has(elementId)) onElementClick(elementId);
        };
        canvas.on('mouse:down', handleMouseDown);
        return () => canvas.off('mouse:down', handleMouseDown);
    }, [canvas, editable, onElementClick, clickableElementIds, elementsMap]);

    // Responsywne skalowanie
    useEffect(() => {
        if (!canvas || !containerRef.current) return;
        // Po zmianie wymiarów stary canvas jest już usunięty, a nowy jeszcze nie trafił do stanu
        if (canvas !== fabricCanvasRef.current) return;

        const resize = () => {
            if (canvas !== fabricCanvasRef.current) return;
            let newScale;

            if (fixedHeight) {
                // Tryb szarfy: skaluj po wysokości, szerokość może wychodzić poza kontener (scroll)
                newScale = fixedHeight / canvasHeight;

                // Użyj canvas.setZoom + setDimensions — poprawne skalowanie Fabric.js bez CSS stretch
                const newWidth = Math.round(canvasWidth * newScale);
                const newHeight = Math.round(canvasHeight * newScale);
                canvas.setZoom(newScale);
                canvas.setDimensions({ width: newWidth, height: newHeight });
                containerRef.current.style.width = `${newWidth}px`;
                containerRef.current.style.height = `${newHeight}px`;
                // Bez centrowania — canvas szerszy niż maxWidth wychodzi poza ekran (negatywny offset)
                containerRef.current.style.justifyContent = 'flex-start';
                containerRef.current.style.maxWidth = 'none';
            } else {
                const wrapperWidth = containerRef.current.clientWidth;
                const wrapperHeight = containerRef.current.parentElement?.clientHeight || window.innerHeight * 0.6;
                const scaleByWidth = wrapperWidth / canvasWidth;
                const scaleByHeight = wrapperHeight / canvasHeight;
                newScale = Math.min(scaleByWidth, scaleByHeight, 1);

                const newWidth = canvasWidth * newScale;
                const newHeight = canvasHeight * newScale;

                if (canvas.lowerCanvasEl) {
                    canvas.lowerCanvasEl.style.width = `${newWidth}px`;
                    canvas.lowerCanvasEl.style.height = `${newHeight}px`;
                }

                const canvasContainer = containerRef.current.querySelector('.canvas-container');
                if (canvasContainer) {
                    canvasContainer.style.width = `${newWidth}px`;
                    canvasContainer.style.height = `${newHeight}px`;
                    canvasContainer.style.margin = '0 auto';
                }

                const upperCanvas = containerRef.current.querySelector('.upper-canvas');
                if (upperCanvas) {
                    upperCanvas.style.width = `${newWidth}px`;
                    upperCanvas.style.height = `${newHeight}px`;
                }

                containerRef.current.style.height = `${newHeight}px`;
                containerRef.current.style.width = '100%';
            }

            setScale(newScale);
            canvas.renderAll();
        };

        resize();
        window.addEventListener('resize', resize);

        // Kontener zmienia szerokość także bez zmiany okna (np. otwarcie panelu ustawień obok podglądu).
        // W trybie szarfy rozmiar zależy tylko od fixedHeight, więc obserwator nie jest potrzebny.
        let resizeObserver;
        let cancelPendingResize = () => {};
        if (!fixedHeight && typeof ResizeObserver !== 'undefined') {
            let lastWidth = containerRef.current.clientWidth;
            let frame = null;
            resizeObserver = new ResizeObserver((entries) => {
                const width = entries[0]?.contentRect.width;
                // Reagujemy tylko na szerokość – wysokość ustawia sam resize()
                if (width && Math.abs(width - lastWidth) > 1) {
                    lastWidth = width;
                    // resize() zmienia wymiary obserwowanego kontenera – wykonujemy go w następnej klatce,
                    // inaczej przeglądarka zgłasza „ResizeObserver loop completed with undelivered notifications”
                    cancelAnimationFrame(frame);
                    frame = requestAnimationFrame(resize);
                }
            });
            resizeObserver.observe(containerRef.current);
            cancelPendingResize = () => cancelAnimationFrame(frame);
        }

        return () => {
            window.removeEventListener('resize', resize);
            resizeObserver?.disconnect();
            cancelPendingResize();
        };
    }, [canvas, canvasWidth, canvasHeight, fixedHeight]);

    // Uchwyty zaznaczenia są rysowane w pikselach canvasa, a podgląd jest pomniejszany przez CSS –
    // bez korekty na telefonie robią się za małe, by złapać je palcem
    useEffect(() => {
        if (!canvas || !editable) return undefined;
        if (canvas !== fabricCanvasRef.current) return undefined;

        const isTouch = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
        const cssScale = fixedHeight ? 1 : Math.max(scale, 0.1);
        const cornerSize = Math.round((isTouch ? 22 : 13) / cssScale);
        const touchCornerSize = Math.round(44 / cssScale);

        const applyControlSize = (object) => {
            object.set({ cornerSize, touchCornerSize, transparentCorners: false });
        };

        canvas.getObjects().forEach(applyControlSize);
        const handleObjectAdded = (event) => {
            if (event.target) applyControlSize(event.target);
        };
        canvas.on('object:added', handleObjectAdded);
        canvas.requestRenderAll();

        return () => canvas.off('object:added', handleObjectAdded);
    }, [canvas, editable, scale, fixedHeight]);

    return (
        <div
            ref={containerRef}
            className={className}
            style={{
                width: '100%',
                maxWidth: `${canvasWidth}px`,
                position: 'relative',
                margin: '0 auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                ...style
            }}
        >
            {isLoading && (
                <div role="status" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    padding: '8px 18px',
                    borderRadius: 999,
                    border: '1px solid #dbe4ec',
                    background: '#ffffff',
                    color: '#1c3f58',
                    fontSize: 15,
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                }}>
                    Ładowanie…
                </div>
            )}
            {highlightBox && (
                <div
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        ...highlightBox,
                        border: '2px solid #2f80c0',
                        borderRadius: 6,
                        background: 'rgba(47, 128, 192, 0.08)',
                        boxShadow: '0 0 0 3px rgba(47, 128, 192, 0.18)',
                        pointerEvents: 'none',
                        transition: 'all 0.15s ease',
                        zIndex: 5,
                    }}
                />
            )}
        </div>
    );
});

FabricRenderer.displayName = 'FabricRenderer';

export default FabricRenderer;
